<script>
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
// import Editor from '@tinymce/tinymce-vue';

import generateDocument from '@/lib/agreement/generate';
import permissions from '@/mixins/permissions';
import featureFlag from '@/mixins/featureFlag';
import { analytics } from '../firebase';
import { editor as Editor, defaultEditorConfig, revisionHistoryPlugins } from '@/lib/ckeditor';
// const VUE_APP_TINY_MCE_API_KEY = process.env.VUE_APP_TINY_MCE_API_KEY;
import { hasEditPermission, currentRoleLevelName } from '@/mixins/roleLevelPermissions';
// import Comments from '@ckeditor/ckeditor5-comments/src/comments';
import getCommentsAdapter from '../lib/CommentsAdapter';
import getTrackChangesAdapter from '../lib/TrackChangesAdapter'; 
import getRevisionHistoryAdapter from '../lib/RevisionHistoryAdapter';
import UsersIntegration from '../lib/UsersIntegration';
import emailApiRequest from '@/lib/emailApiRequest';
import DownloadAgreementConfirmationModal from './DownloadAgreementConfirmationModal.vue';
import agreementTitle from '@/lib/agreement-title';

function convertCountersToText(html) {
	const tempDiv = document.createElement('div');
	tempDiv.innerHTML = html;

	const toRoman = (num) => {
		if (num <= 0) return '';
		const lookup = { m: 1000, cm: 900, d: 500, cd: 400, c: 100, xc: 90, l: 50, xl: 40, x: 10, ix: 9, v: 5, iv: 4, i: 1 };
		let roman = '';
		for (let i in lookup) {
			while (num >= lookup[i]) {
				roman += i;
				num -= lookup[i];
			}
		}
		return roman;
	};

	const toAlpha = (num) => {
		let alpha = '';
		while (num > 0) {
			let mod = (num - 1) % 26;
			alpha = String.fromCharCode(97 + mod) + alpha;
			num = Math.floor((num - 1) / 26);
		}
		return alpha;
	};

	const getNumberingHtml = (className, index, parentFiveIndex) => {
		const i = index + 1;
		switch(className) {
			case 'one': return toAlpha(i).toUpperCase() + '.';
			case 'two': return '(' + i + ')';
			case 'three': return '(' + toAlpha(i) + ')';
			case 'four': return '(' + toRoman(i) + ')';
			case 'five': return i + '.';
			case 'six': return (parentFiveIndex ? parentFiveIndex : '1') + '.' + i;
			case 'seven': return '(' + i + ')';
			case 'eight': return '(' + toAlpha(i) + ')';
			case 'nine': return '(' + toRoman(i) + ')';
			case 'ten': return toAlpha(i).toUpperCase() + '.';
			case 'eleven': return i + '.';
			case 'twelve': return toAlpha(i) + ')';
			default: return i + '.';
		}
	};

	const classLevels = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];

	classLevels.forEach(className => {
		const lists = tempDiv.querySelectorAll(`ol.${className}`);
		lists.forEach(list => {
			let parentFiveIndex = '';
			if (className === 'six') {
				const parentFiveLi = list.closest('ol.five > li');
				if (parentFiveLi) {
					let pLi = parentFiveLi;
					let count = 0;
					while (pLi) {
						if (pLi.tagName && pLi.tagName.toLowerCase() === 'li' && !pLi.classList.contains('skip')) count++;
						pLi = pLi.previousElementSibling;
					}
					parentFiveIndex = count;
				}
			}

			let currentIdx = 0;
			const listItems = Array.from(list.children).filter(child => child.tagName.toLowerCase() === 'li');
			listItems.forEach((li) => {
				if (li.classList.contains('skip')) return;
				if (li.classList.contains('reset-to-7')) currentIdx = 6;
				else if (li.classList.contains('reset-to-8')) currentIdx = 7;
				
				const numberingText = getNumberingHtml(className, currentIdx, parentFiveIndex);
				currentIdx++;
				
				let targetNode = li;
				if (targetNode.firstElementChild && (targetNode.firstElementChild.tagName.toLowerCase() === 'p' || targetNode.firstElementChild.tagName.toLowerCase() === 'span')) {
					targetNode = targetNode.firstElementChild;
				}
				
				// Revert to plain text prepending, and add a single non-breaking space and a regular space to mimic indent slightly naturally if CSS styles fail
				targetNode.insertBefore(document.createTextNode(numberingText + '\u00A0\u00A0\u00A0'), targetNode.firstChild);
			});
		});
	});

	const processSuffixes = (selector, getSuffixHtml) => {
		const lists = tempDiv.querySelectorAll(selector);
		lists.forEach(list => {
			const lis = Array.from(list.children).filter(child => child.tagName.toLowerCase() === 'li');
			const total = lis.length;
			lis.forEach((li, index) => {
				const hasOlChild = Array.from(li.children).some(c => c.tagName.toLowerCase() === 'ol');
				const suffix = getSuffixHtml(li, index, total, hasOlChild);
				if (suffix) {
					let targetNode = li;
					if (targetNode.lastElementChild && (targetNode.lastElementChild.tagName.toLowerCase() === 'p' || targetNode.lastElementChild.tagName.toLowerCase() === 'span')) {
						targetNode = targetNode.lastElementChild;
					}
					targetNode.appendChild(document.createTextNode(suffix));
				}
			});
		});
	};

	processSuffixes('ol.andList, ul.andList', (li, i, total, hasOlChild) => {
		if (hasOlChild && i !== total - 1 && i !== total - 2) return '';
		if (i === total - 2) return '; and';
		if (i === total - 1) return '.';
		return ';';
	});
	
	processSuffixes('ol.period-andList, ul.period-andList', (li, i, total, hasOlChild) => {
		if (hasOlChild && i !== total - 2) return '';
		if (i === total - 2) return '; and';
		if (i === total - 1) return '.';
		return ';';
	});

	processSuffixes('ol.semiColon-andList, ul.semiColon-andList', (li, i, total, hasOlChild) => {
		if (hasOlChild && i !== total - 1 && i !== total - 2) return '';
		if (i === total - 2) return '; and';
		return ';';
	});

	processSuffixes('ol.period-orList, ul.period-orList', (li, i, total, hasOlChild) => {
		if (hasOlChild && i !== total - 1 && i !== total - 2) return '';
		if (i === total - 2) return '; or';
		if (i === total - 1) return '.';
		return ';';
	});

	processSuffixes('ol.colonList, ul.colonList', (li, i, total, hasOlChild) => {
		if (li.classList.contains('no-colon')) return '';
		if (i === total - 1) return '.';
		return ':';
	});

	processSuffixes('ol.doubleAnd-semiColonList, ul.doubleAnd-semiColonList', (li, i, total, hasOlChild) => {
		if (i === total - 1) return '; and';
		if (i === total - 2 && i !== 0) return '; and';
		return ';';
	});

	processSuffixes('ol.colon-semiColon-andList, ul.colon-semiColon-andList', (li, i, total, hasOlChild) => {
		if (li.classList.contains('no-colon') && i === 0) return '';
		if (li.classList.contains('no-colon') && i === total - 2) return '';
		if (i === 0) return ':';
		if (i === total - 2) return '; and';
		if (i === total - 1) return '.';
		return ';';
	});

	processSuffixes('ol.offerToPurchaseList, ul.offerToPurchaseList', (li, i, total, hasOlChild) => {
		if (i === 0) return ';';
		if (i === total - 2 && i !== 1) return '; and';
		if (i === total - 1) return '.';
		return '';
	});

	return tempDiv.innerHTML;
}

export default {
  components: { DownloadAgreementConfirmationModal },
	props: [
		'agreementData'
	],
	name: 'Collaboration',
	mixins: [permissions, featureFlag],
	computed: {
		revisionHistoryEnabled() {
			return this.featureEnabled('feature_revision_history');
		},
		...mapState(['agreement', 'user', 'editedAgreement','currentAgreementRoleLevelName','permissions','isEditorUnresolved','revisions']),
		isAgreementOwner() {
			return this.permissions[this.user.uid]?.primaryLawyer;
		},
		editorData: {
			get: function () {
				if (this.editedAgreement) {
					return this.editedAgreement;
				} else {
					return generateDocument({
						agreementData: this.agreementData,
					});
				}
			},
			set: function (value) {
				this.editorHtml = value;
			},
		},
		canDownload() {
			return this.editedAgreement;
		},
	},
	data() {
		return {
			editorHtml: '',
			// VUE_APP_TINY_MCE_API_KEY,
			classLevels: [
				'one',
				'two',
				'three',
				'four',
				'five',
				'six',
				'seven',
				'eight',
				'nine',
				'ten',
				'eleven',
			],
			editor: Editor,
			isViewMode : !hasEditPermission('editor'),
			editorInstance: null,
			isNotifyClientBtnDisabled: false,
			showDownloadConfirmationModal: false,
			downloadFormat: 'pdf',
			loading:true,
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName)
		};
	},
	mounted() {
		this._autoRev = {
			beforeUnloadHandler: null,
			savingInFlight: null,
		};

		analytics.logEvent('screen_view', {
			firebase_screen: 'Editor',
			firebase_screen_class: 'Collaboration',
		});
				
		Promise.all([
			this.$store.dispatch('fetchAgreement',{id: this.$route.params.id}),
			this.$store.dispatch('fetchEditedAgreement', {id: this.$route.params.id}),
			this.revisionHistoryEnabled && this.$store.dispatch('fetchRevisions', {id: this.$route.params.id})
		])
        .catch((error) => {
            console.error('Error fetching agreement data:', error);
        })
        .finally(() => {
			this.loading = false;
        });

		
		if(!this.agreementData.hasOwnProperty('comments')){
			this.$set(this.agreementData, 'comments', []);
		}
		if(!this.agreementData.hasOwnProperty('trackedChanges')){
			this.$set(this.agreementData, 'trackedChanges', []);
		}
	},
	beforeDestroy() {
		this._saveSessionRevision('destroy');

		const state = this._autoRev;
		if (state && state.beforeUnloadHandler) {
			window.removeEventListener('beforeunload', state.beforeUnloadHandler);
		}
		if (this.$store.state.revisionFlushCallback) {
			this.$store.commit('setRevisionFlushCallback', null);
		}
	},
	beforeRouteLeave(to, from, next) {
		this._saveSessionRevision('route-leave')
			.catch((error) => {
				console.error('[session-revision] route-leave save failed:', error);
			})
			.finally(() => next());
	},
	methods: {
		initEditorInCKUpdated(editor){
			let classesWhilePressingEnter;
			// editor.commands.get( 'enter' ).on( 'execute', () => {
			// editor.model.change( writer => {
			// const position = editor.model.document.selection.getFirstPosition();
			// writer.insertText( ';', position );
			// } );

			// const selectedBlocks = Array.from( editor.model.document.selection.getSelectedBlocks() );
			// const firstSelectedBlock = selectedBlocks[ 0 ];

			// if ( firstSelectedBlock && firstSelectedBlock.hasAttribute( 'listType' ) ) {
			// 	classesWhilePressingEnter = firstSelectedBlock.getAttribute( 'htmlListAttributes' ).classes;
			// } else {
			// 	classesWhilePressingEnter;
			// }
			// }, { priority: 'high' } );
			this.editorInstance = editor

			const exportCommand = editor.commands.get('exportWord');
			if (exportCommand) {
				const originalExecute = exportCommand.execute.bind(exportCommand);
				exportCommand.execute = function(options = {}) {
					const html = editor.getData();
					const transformedHtml = convertCountersToText(html);
					options.dataCallback = () => transformedHtml;
					return originalExecute(options);
				};
			}


			this._autoRev.beforeUnloadHandler = () => this._saveSessionRevision('unload');
			window.addEventListener('beforeunload', this._autoRev.beforeUnloadHandler);

			// Awaited by store.logout() before signOut so the Firestore write
			// lands while the user is still authenticated.
			this.$store.commit(
				'setRevisionFlushCallback',
				() => this._saveSessionRevision('logout')
			);

			if(!this.isAgreementOwner || this.isViewMode){
				editor.commands.get('trackChanges').execute();
			}
			const bodyWrapper = document.querySelector('.ck-body-wrapper');
            if (bodyWrapper && this.isViewMode=== true) {
                bodyWrapper.classList.add('editor-view-mode');
            }

			this.$nextTick(() => {
				this.setIsEditorUnresolved();
			});

			editor.editing.view.document.on( 'keydown', ( event, data ) => {

				if (data.keyCode === 9 && data.shiftKey === false) {
					data.preventDefault();
					editor.execute( 'indentList' );
					if (classesWhilePressingEnter){
						this.classLevels.some((className, index) => {
							if (classesWhilePressingEnter.includes(className) ) {
								const selectedBlocks = Array.from( editor.model.document.selection.getSelectedBlocks() );
								const firstSelectedBlock = selectedBlocks[ 0 ];

								if ( firstSelectedBlock && firstSelectedBlock.hasAttribute( 'listType' ) ) {
									editor.model.change( writer => {
									const htmlListAttributes = firstSelectedBlock.getAttribute( 'htmlListAttributes' );

								if ( htmlListAttributes.classes ) {
									htmlListAttributes.push( this.classLevels[index + 1] );
								} else {
									htmlListAttributes.classes = [this.classLevels[index + 1]];
								}
									writer.setAttribute( 'htmlListAttributes', htmlListAttributes, firstSelectedBlock );
								} );

									editor.editing.reconvertItem( firstSelectedBlock );
								}
							}
						})
					}
				} else if(data.keyCode === 13){
					const selectedBlocks = Array.from( editor.model.document.selection.getSelectedBlocks() );
					const firstSelectedBlock = selectedBlocks[ 0 ];

					if ( firstSelectedBlock && firstSelectedBlock.hasAttribute( 'listType' ) ) {
						classesWhilePressingEnter = firstSelectedBlock.getAttribute( 'htmlListAttributes' ).classes;
					} else {
						classesWhilePressingEnter;
					}
				}
			}, { priority: 'high' } );
		},
		_saveSessionRevision(reason) {
			const state = this._autoRev;
			if (!state || !this.editorInstance) return Promise.resolve();


			const tracker = this.editorInstance.plugins.has('RevisionTracker')
				? this.editorInstance.plugins.get('RevisionTracker')
				: null;
			if (!tracker || !tracker.currentRevision) return Promise.resolve();

			const cur = tracker.currentRevision;
			if (cur.fromVersion === cur.toVersion) return Promise.resolve();

			// Dedup concurrent exit hooks (beforeRouteLeave + beforeDestroy).
			if (state.savingInFlight) return state.savingInFlight;

			const promise = tracker
				.saveRevision({
					name: this._autoRevisionName(),
					attributes: { auto: true, reason },
				})
				.catch((error) => {
					console.error('[session-revision] saveRevision failed:', error);
				})
				.finally(() => {
					state.savingInFlight = null;
				});

			state.savingInFlight = promise;
			return promise;
		},
		_autoRevisionName() {
			const who = (this.user && this.user.displayName) || 'Auto-save';
			const when = new Date().toLocaleString(undefined, {
				hour: '2-digit',
				minute: '2-digit',
				month: 'short',
				day: 'numeric',
			});
			return `${who} · ${when}`;
		},
		// onExecCommand() {},
		async save() {
			this.$store
				.dispatch('updateEditedAgreement', this.editorHtml)
				.then(() => {
					analytics.logEvent('edited_agreement_saved');
				});
			let editedAgreementWithDiscardedSuggestions = await this.editorInstance.plugins.get('TrackChangesData').getDataWithDiscardedSuggestions()
			this.$store.dispatch('updateEditedAgreementWithDiscardedSuggestions', editedAgreementWithDiscardedSuggestions)
		},
		download() {
			window.open(
				`${window.location.origin}${window.location.pathname}/preview`
			);
		},
		downloadDraft(){
			this.downloadFormat = 'pdf'
			if(this.isEditorUnresolved){
				this.showDownloadConfirmationModal = true
			}
			else{
				this.downloadAgreement()
			}

		},
		downloadAgreement(){
			window.open(
				`${window.location.origin}${window.location.pathname}/preview?draft=true`
			);
			this.showDownloadConfirmationModal = false
		},
		downloadDraftWord(){
			this.downloadFormat = 'docx'
			if(this.isEditorUnresolved){
				this.showDownloadConfirmationModal = true
			}
			else{
				this.downloadAgreementWord()
			}
		},
		downloadAgreementWord(){
			window.open(
				`${window.location.origin}${window.location.pathname}/preview?draft=true&format=docx`
			);
			this.showDownloadConfirmationModal = false
		},
		update() {
			const newElement = this.$createElement;
			const messageConent = newElement(
				'div',
				{ class: ['text-center'] },
				[
					newElement(
						'p',
						{},
						'Updating will pull any new data from the Agreement Builder into the editor and any changes will be lost.'
					),
				]
			);
			this.$bvModal
				.msgBoxConfirm([messageConent], {
					title: 'Heads Up!',
					size: 'md',
					buttonSize: 'sm',
					okVariant: 'primary',
					okTitle: 'I Understand',
					cancelTitle: "I'm not ready",
					footerClass: 'p-2',
					hideHeaderClose: false,
					centered: true,
				})
				.then(async(accepted) => {
					if (accepted) {
						const updatedAgreement = generateDocument({
							agreementData: this.agreementData,
						});
						this.editorHtml = updatedAgreement;
						await this.$store.dispatch(
							'updateEditedAgreement',
							updatedAgreement
						);
						if (this.editorInstance) {
							this.editorInstance.setData(updatedAgreement);
						}
						analytics.logEvent('edited_agreement_updated');
						let editedAgreementWithDiscardedSuggestions = await this.editorInstance.plugins.get('TrackChangesData').getDataWithDiscardedSuggestions()
						this.$store.dispatch('updateEditedAgreementWithDiscardedSuggestions', editedAgreementWithDiscardedSuggestions)
					}
				})
				.catch(() => {});
		},
		async exportToClio(){
			try
			{
				const clioExport = firebaseApp.functions('northamerica-northeast1').httpsCallable('clioApis-clioExport');
				const accessTokenClio=JSON.parse(localStorage.getItem('cliotoken'))
				let header={
					authorization: accessTokenClio.access_token,
					"name": "Divii Uploads",
					"parent": {
						"id": 0,
						"type": 'Folder'
					},
					"fileName": "SeparationAgreement.pdf",
					"restore": false
				}
				const clioUploadSuccess = await clioExport(header)
				.then(linkResponse=>{
					if(linkResponse){
					}
				});
				console.log("clioUploadSuccess:", clioUploadSuccess)
				
			}catch(error){
				console.log("error folder uploading:",error)
			}
			// this.download();
		},
		async notifyOtherUsers(){
			let body={
				agreementId: this.agreement.id,
				host: window.location.host,
				UserEmail: this.user.email,
				UserName: this.user.displayName,
				agreementName: this.agreement && agreementTitle(this.agreement).co != 'and'
					? agreementTitle(this.agreement)
					: '',
				pageName: "Editor"
			}
			console.log('body',body);
			this.isNotifyClientBtnDisabled = true;
			try {
				await emailApiRequest('POST','/notifyEditorUpdateEmail',body).catch((err)=> console.log('notifyEditorUpdateEmail err->',err));
					this.isNotifyClientBtnDisabled = false;
			} catch (error) {
				this.isNotifyClientBtnDisabled = false;
			}
		},
		buildEditorToolbar(){
			if (this.isViewMode) {
				return ['comment'];
			}
			const toolbar = [
				'bold',
				'italic',
				'underline',
				'strikethrough',
				'link',
				'|',
				'bulletedList',
				'numberedList',
				'|',
				'outdent',
				'indent',
				'alignment',
				'|',
				'fontColor',
				'fontBackgroundColor',
				'fontFamily',
				'fontSize',
				'highlight',
				'findAndReplace',
				'|',
				'removeFormat',
				'subscript',
				'superscript',
				'specialCharacters',
				'horizontalLine',
				'blockQuote',
				'pageBreak',
				'|',
				'undo',
				'redo',
				'|',
				'comment',
				'commentsArchive',
				'wproofreader',
			];
			if (this.isAgreementOwner) {
				toolbar.push('trackChanges');
			}
			if (this.isAgreementOwner && this.revisionHistoryEnabled) {
				toolbar.push('|');
				toolbar.push('revisionHistory');
			}
			return toolbar;
		},
		getEditorConfig(){
			const config = {
				toolbar: this.buildEditorToolbar(),
				extraPlugins: [
					UsersIntegration(this.agreementData,'editor'),
					getCommentsAdapter(this.agreementData,'editor'),
					getTrackChangesAdapter(this.agreementData,'editor'),
				],
				htmlSupport: {
					allow: [
						{
							name: 'div',
							attributes: true,
							classes: true,
							id:true
						},
						{
							name: 'span',
							attributes: true,
							classes: true
						},
						{
							name: 'p',
							attributes: true,
							classes: true
						},
						{
							name: 'li',
							attributes: true,
							classes: true
						},
						{
							name: 'ol',
							attributes: true,
							classes: true 
						},
						{
							name: 'b',
							attributes: true,
							classes: true 
						},
						{
							name: 'i'
						},
						{
							name: 'table',
							attributes: true,
							classes: true,
						},
						{
							name: 'tbody',
							attributes: true,
							classes: true ,
						},
						{
							name: 'tr',
							attributes: true,
							classes: true,
						},
						{
							name: 'td',
							attributes: true,
							classes: true,
						}
					]
				},
				autoParagraph :false,
				fillEmptyBlocks : false,
				highlight: {
					options: [
						{
							model: 'greenMarker',
							class: 'marker-green',
							title: 'Green marker',
							color: 'rgb(25, 156, 25)',
							type: 'marker'
						},
						{
							model: 'yellowMarker',
							class: 'marker-yellow',
							title: 'Yellow marker',
							color: '#cac407',
							type: 'marker'
						}
					]
				},
				table: {
					tableProperties: {
						// The default styles for tables in the editor.
						// They should be synchronized with the content styles.
						defaultProperties: {
							borderStyle: 'solid',
							borderColor: 'hsl(90, 75%, 60%)',
							borderWidth: '3px',
							alignment: 'left',
							width: '550px',
							height: '450px'
						},
						// The default styles for table cells in the editor.
						// They should be synchronized with the content styles.
						tableCellProperties: {
							defaultProperties: {
								horizontalAlignment: 'center',
								verticalAlignment: 'bottom',
								padding: '10px'
							}
						}
					}
				},
				// channelId scopes collaboration features (comments, track changes,
				// revision history) to this specific agreement document.
				collaboration: {
					channelId: this.agreementData.id,
				},
			};
			if (!this.revisionHistoryEnabled) {
				return config;
			}
			config.extraPlugins.push(
				...revisionHistoryPlugins,
				getRevisionHistoryAdapter(this.agreementData, this.revisions)
			);
			// Required by RevisionHistory: DOM containers for the viewer panel.
			// document.querySelector is safe here because getEditorConfig() is
			// only called after the v-if renders the elements into the DOM.
			config.revisionHistory = {
				editorContainer: document.querySelector('#ck-editor-container'),
				viewerContainer: document.querySelector('#revision-viewer-container'),
				viewerEditorElement: document.querySelector('#revision-viewer-editor'),
				viewerSidebarContainer: document.querySelector('#revision-viewer-sidebar'),
				resumeUnsavedRevision: true,
				viewerEditorConfig: {
					extraPlugins: [
						UsersIntegration(this.agreementData,'editor'),
						getCommentsAdapter(this.agreementData,'editor'),
						getTrackChangesAdapter(this.agreementData,'editor'),
					]
				}
			};
			config.autosave = {
				save: (editor) => {
					return editor.plugins.get('RevisionTracker').update();
				}
			};
			return config;
		},
		setIsEditorUnresolved(){
			// Guard: the watcher can fire before the editor is ready (editorInstance
			// is set in the @ready callback). Skip if editor hasn't initialised yet.
			if (!this.editorInstance) return;
			const trackChangesPlugin = this.editorInstance.plugins.get( 'TrackChanges' );
			let unresolvedChanges = trackChangesPlugin.getSuggestions( { skipNotAttached: true, toJSON: true } );
			const commentsRepository = this.editorInstance.plugins.get('CommentsRepository');
			const commentThreadsData = commentsRepository.getCommentThreads({
				skipNotAttached: true,
				skipEmpty: true,
				toJSON: true,
			});
			const unresolvedComments = commentThreadsData.filter((comments)=> !comments.resolvedBy && !comments.resolvedAt)
			this.$store.commit('setIsEditorUnresolved',(unresolvedChanges.length > 0 || unresolvedComments.length > 0))
		}
	},
	watch: {
		editorHtml: {
			handler: debounce(function () {
				this.save();
				this.setIsEditorUnresolved()
			}, 1000),
		},
		'agreementData.comments' :{
			handler(){
				this.setIsEditorUnresolved()
			}
		}
	},
};
</script>

<style>
@import '../assets/css/printstyles.css';

#container {
    /* To create the column layout. */
    display: flex;

    /* To make the container relative to its children. */
    position: relative;
    max-height: calc(100vh-100px);
}
/* ::marker{
	content: '';
} */

/* .one > li  br,.two > li br,.three > li br,.four > li br,.five > li br,.six > li br,.seven > li br,.eight > li br,.nine > li br{
	content: '';
} */

/* .seven > li > .ck-list-bogus-paragraph, .eight > li > .ck-list-bogus-paragraph{
	display: unset !important;
} */

.one > li::marker,.two > li::marker,.three > li::marker,.four > li::marker,.five > li::marker,.six > li::marker,.seven > li::marker,.eight > li::marker,.nine > li::marker,.ten > li::marker,.eleven > li::marker,.twelve > li::marker{
	content: '';
}

.ck-editor__editable .ck-list-bogus-paragraph{
	display: unset !important;
}

.ck-editor__editable .ck-list-bogus-paragraph:has(> br){
	display: -webkit-inline-box !important;
}

p:empty {
  display:none
}

#ck-editor-container .ck-editor__top{
	/* position: fixed !important;
	bottom:0 !important; */
	position: sticky !important;
    top: 0px !important;
    z-index: 999 !important;
}

#ck-editor-container .ck-editor{
	margin-top: 1rem;
    overflow-y: scroll;
    max-height: 65vh;
}

@media (max-width: 768px) {
    #ck-editor-container .ck-editor{
		padding-bottom: 4.5rem !important;
    }
	#ck-editor-container .ck-editor::-webkit-scrollbar{
		width: 0.7rem !important;
	}
}
.notify-client{
	position: fixed; 
	bottom: 2rem; 
	right: 2.5rem;      
}

.editor-view-mode .ck-suggestion--accept,
.editor-view-mode .ck-suggestion--discard {
    display: none !important;
}

/* ── Revision History viewer ─────────────────────────────────────
   CKEditor sets inline style="display: block" on the viewerContainer
   when opening revision history. Since inline styles beat class-based
   CSS, we use [style*="block"] + !important to force display:flex so
   the sidebar stays beside the editor (row layout), not below it.
*/
.editor-container {
    position: relative;
}

/* Default hidden state */
#revision-viewer-container {
    display: none;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    margin-top: 0.5rem;
    min-height: 65vh;
    max-height: 80vh;
    overflow: hidden;
    flex-direction: row;
    align-items: stretch;
}

/* CKEditor sets style="display: block" inline when it shows the viewer.
   Override with display:flex !important to restore side-by-side layout. */
#revision-viewer-container[style*="block"] {
    display: flex !important;
}

/* The viewer's CKEditor instance — fills the left side */
.revision-viewer-editor {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
}

/* CKEditor's .ck-editor inside the viewer — no max-height/overflow constraints */
.revision-viewer-editor .ck-editor {
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
    max-height: none !important;
    overflow: visible !important;
}

.revision-viewer-editor .ck-editor__editable {
    flex: 1;
    overflow-y: auto;
    min-height: 200px;
}

/* Right sidebar: shows list of revisions.
   padding-bottom keeps the last revision card scrollable past the
   fixed-position .notify-client button that overlays the viewport's
   bottom-right corner. */
.revision-viewer-sidebar {
    width: 280px;
    min-width: 220px;
    border-left: 1px solid #c4c4c4;
    overflow-y: auto;
    background: #fafafa;
    flex-shrink: 0;
    padding-bottom: 5rem;
}
</style>

<template>
	<div>
		<b-card class="mt-2 custom-header-wrapper">
			<div slot="header" class="section-heading">
				<h4>Editor</h4>
				<a href="#" onclick="Intercom('showArticle', 11203380)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
			</div>
		</b-card>
		<b-row class="my-3">
			<b-col cols="12">
				<div class="d-flex justify-content-end flex-wrap" style="gap: .5rem" data-testid="editor-buttons">
					<b-button
						variant="secondary"
						@click="update"
						data-testid="editor-update-button"
						v-if="currentAgreementRoleLevelName === 'lawyer-level-1' || currentAgreementRoleLevelName === 'client-level-1'"
						:disabled="isViewMode"
					>
						Revert to Original
					</b-button>
					<b-button
						variant="secondary"
						data-testid="editor-download-draft"
						@click="downloadDraft"
					>
						Download Draft
					</b-button>
					<b-button
						variant="pastel-blue"
						data-testid="editor-download-draft-word"
						@click="downloadDraftWord"
					>
						Download (Word)
					</b-button>
					<!-- <b-button
						variant="primary"
						@click="exportToClio"
						data-testid="export-to-clio"
					>
						Export To Clio
					</b-button> -->
					<!-- <b-button
						variant="primary"
						:disabled="!canDownload"
						@click="download"
						data-testid="editor-download"
					>
						Download
					</b-button> -->
				</div>
			</b-col>
		</b-row>
		<!-- Outer wrapper that stays visible at all times.
		     #ck-editor-container is the live editor area that CKEditor's
		     RevisionHistory plugin hiddes when the viewer is active.
		     The viewer divs are siblings outside of #ck-editor-container
		     so they remain visible even when CKEitor hides the editor. -->
		<div id="editor-container" class="editor-container">
			<!-- Live CKEditor wrapper — CKEditor hides this div when revision history opens -->
			<div id="ck-editor-container">
				<div v-if="!loading && editorData" class="preview">
					<ckeditor :editor="editor" v-model="editorData" :config="getEditorConfig()" 
						@ready="initEditorInCKUpdated"></ckeditor>
				</div>
				<div
					v-else
					class="
						text-center
						w-100
						d-flex
						justify-content-center
						align-items-center
					"
					style="height: 500px"
				>
					<b-spinner
						style="width: 3rem; height: 3rem"
						variant="primary"
						label="Loading..."
					/>
				</div>
			</div>

			<!-- Revision History viewer containers.
			     CKEditor toggles display on these when the user opens/closes
			     revision history. They are siblings of #ck-editor-container
			     so CKEditor can swap them, and the outer wrapper stays visible. -->
			<div id="revision-viewer-container" class="revision-viewer-container">
				<div id="revision-viewer-editor" class="revision-viewer-editor"></div>
				<div id="revision-viewer-sidebar" class="revision-viewer-sidebar"></div>
			</div>
		</div>
		<b-button variant="primary" class="notify-client" @click="notifyOtherUsers" :disabled="isNotifyClientBtnDisabled">
			<template v-if="isNotifyClientBtnDisabled">
				<b-spinner small type="grow"></b-spinner>
				Notifying...
			</template>
			<template v-else>
				Notify Collaborators
			</template>
		</b-button>
		<download-agreement-confirmation-modal
			:showDownloadConfirmationModal="showDownloadConfirmationModal"
			:draft="true"
			@close="showDownloadConfirmationModal= false"
			:mode="'editor'"
			:format="downloadFormat"
		/>
	</div>
</template>
